/**
 * SuperAdmin pricing catalog — `/platform/pricing`.
 *
 * Two cards: current tier prices (editable by SUPER_ADMIN) and the
 * singleton pricing config (tax mode / default currency / discount).
 * Saving a new price supersedes the existing (tier, currency) row — old
 * rows stay in history for audit.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { apiClient } from '@services/api-client';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface PricingRow {
  id: string;
  tier: string;
  currency: string;
  monthlyAmount: string;
  annualAmount: string;
  effectiveFrom: string;
  supersededAt: string | null;
  notes: string | null;
}

interface PricingConfig {
  taxMode: 'inclusive' | 'exclusive' | 'disabled';
  defaultCurrency: string;
  supportedCurrencies: string[];
  annualDiscountPercent: number;
}

export default function PlatformPricingPage() {
  const [rows, setRows] = useState<PricingRow[]>([]);
  const [config, setConfig] = useState<PricingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<Partial<PricingRow> | null>(null);
  const [editingConfig, setEditingConfig] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [pricesRes, configRes] = await Promise.all([
        apiClient.get('/admin/pricing'),
        apiClient.get('/admin/pricing-config'),
      ]);
      setRows(pricesRes.data || []);
      setConfig(configRes.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Pricing catalog</h1>
      <p className="text-sm text-gray-600">
        SuperAdmin-configurable per-tier pricing. New prices supersede
        the current effective row for their (tier, currency); existing
        tenants keep their locked-in <code>pricingSnapshot</code>.
      </p>

      {error && <Card className="p-4 bg-red-50 text-red-800 text-sm">{error}</Card>}

      {loading && <Spinner />}

      {!loading && (
        <>
          <PricingConfigCard
            config={config}
            onEdit={() => setEditingConfig(true)}
          />

          <Card className="overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <h2 className="font-semibold">Tier prices (current)</h2>
              <Button
                onClick={() =>
                  setEditRow({
                    tier: 'growth',
                    currency: config?.defaultCurrency ?? 'USD',
                    monthlyAmount: '',
                    annualAmount: '',
                  })
                }
              >
                + Set new price
              </Button>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2">Currency</th>
                  <th className="px-4 py-2">Monthly</th>
                  <th className="px-4 py-2">Annual</th>
                  <th className="px-4 py-2">Effective from</th>
                  <th className="px-4 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                      No prices configured. Click "+ Set new price" to add one.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 capitalize">{r.tier}</td>
                    <td className="px-4 py-2">{r.currency}</td>
                    <td className="px-4 py-2 font-mono">{r.monthlyAmount}</td>
                    <td className="px-4 py-2 font-mono">{r.annualAmount}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">
                      {new Date(r.effectiveFrom).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{r.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {editRow && (
        <PriceEditor
          initial={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => {
            setEditRow(null);
            refresh();
          }}
        />
      )}

      {editingConfig && config && (
        <ConfigEditor
          initial={config}
          onClose={() => setEditingConfig(false)}
          onSaved={() => {
            setEditingConfig(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function PricingConfigCard({
  config,
  onEdit,
}: {
  config: PricingConfig | null;
  onEdit: () => void;
}) {
  if (!config) {
    return (
      <Card className="p-4 text-sm text-gray-500">Loading config…</Card>
    );
  }
  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Global pricing config</h2>
        <Button variant="outline" onClick={onEdit}>Edit</Button>
      </div>
      <div className="grid grid-cols-4 gap-3 text-sm">
        <Stat label="Tax mode" value={config.taxMode} />
        <Stat label="Default currency" value={config.defaultCurrency} />
        <Stat
          label="Supported currencies"
          value={config.supportedCurrencies.join(', ') || '—'}
        />
        <Stat
          label="Annual discount"
          value={`${config.annualDiscountPercent}%`}
        />
      </div>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function PriceEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: Partial<PricingRow>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tier, setTier] = useState(initial.tier || 'growth');
  const [currency, setCurrency] = useState(initial.currency || 'USD');
  const [monthly, setMonthly] = useState(initial.monthlyAmount || '');
  const [annual, setAnnual] = useState(initial.annualAmount || '');
  const [notes, setNotes] = useState(initial.notes || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await apiClient.post('/admin/pricing', {
        tier,
        currency: currency.toUpperCase(),
        monthlyAmount: monthly,
        annualAmount: annual,
        notes: notes || undefined,
      });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Set tier price" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <label className="block">
          <span className="text-gray-700">Tier</span>
          <ThemedSelect
            className="mt-1 w-full border rounded px-2 py-1"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
          >
            <option value="free">Free</option>
            <option value="growth">Growth</option>
            <option value="professional">Professional</option>
          </ThemedSelect>
        </label>
        <TextInput label="Currency (ISO)" value={currency} onChange={setCurrency} placeholder="USD" />
        <TextInput label="Monthly amount" value={monthly} onChange={setMonthly} placeholder="0.00" />
        <TextInput label="Annual amount" value={annual} onChange={setAnnual} placeholder="0.00" />
      </div>
      <label className="block text-sm mt-3">
        <span className="text-gray-700">Notes</span>
        <input
          className="mt-1 w-full border rounded px-2 py-1"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>
      {err && <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2 mt-3">{err}</div>}
      <ModalFooter busy={busy} onClose={onClose} onSave={save} saveLabel="Set price" />
    </ModalShell>
  );
}

function ConfigEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: PricingConfig;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [taxMode, setTaxMode] = useState(initial.taxMode);
  const [defaultCurrency, setDefaultCurrency] = useState(initial.defaultCurrency);
  const [supported, setSupported] = useState(initial.supportedCurrencies.join(', '));
  const [discount, setDiscount] = useState(String(initial.annualDiscountPercent));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await apiClient.put('/admin/pricing-config', {
        taxMode,
        defaultCurrency: defaultCurrency.toUpperCase(),
        supportedCurrencies: supported
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
        annualDiscountPercent: Number(discount),
      });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Edit pricing config" onClose={onClose}>
      <label className="block text-sm">
        <span className="text-gray-700">Tax mode</span>
        <ThemedSelect
          className="mt-1 w-full border rounded px-2 py-1"
          value={taxMode}
          onChange={(e) => setTaxMode(e.target.value as PricingConfig['taxMode'])}
        >
          <option value="disabled">Disabled (no tax)</option>
          <option value="inclusive">Inclusive (price includes tax)</option>
          <option value="exclusive">Exclusive (+tax)</option>
        </ThemedSelect>
      </label>
      <TextInput label="Default currency" value={defaultCurrency} onChange={setDefaultCurrency} />
      <TextInput
        label="Supported currencies (comma-separated ISO)"
        value={supported}
        onChange={setSupported}
      />
      <TextInput
        label="Annual discount %"
        value={discount}
        onChange={setDiscount}
      />
      {err && <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2 mt-3">{err}</div>}
      <ModalFooter busy={busy} onClose={onClose} onSave={save} saveLabel="Save config" />
    </ModalShell>
  );
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({
  busy,
  onClose,
  onSave,
  saveLabel,
}: {
  busy: boolean;
  onClose: () => void;
  onSave: () => void;
  saveLabel: string;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
      <Button onClick={onSave} disabled={busy}>{busy ? 'Saving…' : saveLabel}</Button>
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="text-gray-700">{label}</span>
      <input
        className="mt-1 w-full border rounded px-2 py-1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}
